import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  Collapse,
  CircularProgress,
  Alert,
} from '@mui/material';
import { VolumeOff as SilenceIcon } from '@mui/icons-material';
import { CARD_BORDER_RADIUS } from '../types';
import { CreateSilenceRequest, SilenceMatcher } from '../../../../../api/cansense/silenceApi';
// Use the detailed Exporter type that includes prometheus_job and target_host
import { Exporter } from '../../types/exporter';

// ============================================================================
// Types
// ============================================================================

export interface CreateSilenceFormData {
  silenceType: 'exporter' | 'labels';
  exporterId?: number;
  labelMatchers?: string;
  durationMinutes: number;
  customHours?: number;
  customMinutes?: number;
  comment: string;
}

export interface CreateSilenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSilenceRequest) => Promise<void>;
  exporters: Exporter[];
  preselectedExporterId?: number;
  loading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const durationPresets = [
  { label: '1 hr', value: 60 },
  { label: '4 hrs', value: 240 },
  { label: '24 hrs', value: 1440 },
  { label: '1 week', value: 10080 },
  { label: 'Custom', value: -1 },
];

// ============================================================================
// Component
// ============================================================================

/**
 * CreateSilenceDialog - Dialog for creating a new alert silence.
 *
 * Features:
 * - Silence by specific exporter OR by label matchers
 * - Duration presets (1hr, 4hrs, 24hrs, 1 week, custom)
 * - Custom duration with hours/minutes inputs
 * - Required comment field
 * - Pre-select exporter if provided
 */
export function CreateSilenceDialog({
  open,
  onClose,
  onSubmit,
  exporters,
  preselectedExporterId,
  loading = false,
}: CreateSilenceDialogProps) {
  const [silenceType, setSilenceType] = useState<'exporter' | 'labels'>('exporter');
  const [selectedExporter, setSelectedExporter] = useState<Exporter | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(60);
  const [customHours, setCustomHours] = useState<string>('');
  const [customMinutes, setCustomMinutes] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [labelMatchers, setLabelMatchers] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [longDurationConfirmed, setLongDurationConfirmed] = useState<boolean>(false);

  // Pre-select exporter if provided
  useEffect(() => {
    if (preselectedExporterId && exporters.length > 0) {
      const exporter = exporters.find((e) => e.id === preselectedExporterId);
      if (exporter) {
        setSelectedExporter(exporter);
        setSilenceType('exporter');
      }
    }
  }, [preselectedExporterId, exporters]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (!preselectedExporterId) {
        setSelectedExporter(null);
      }
      setSilenceType('exporter');
      setSelectedDuration(60);
      setCustomHours('');
      setCustomMinutes('');
      setComment('');
      setLabelMatchers('');
      setError('');
      setLongDurationConfirmed(false);
    }
  }, [open, preselectedExporterId]);

  // Reset long duration warning when duration changes
  const handleDurationChange = (value: number) => {
    setSelectedDuration(value);
    setLongDurationConfirmed(false);
    setError('');
  };

  const showCustomFields = selectedDuration === -1;

  /**
   * Generate Prometheus matchers from an exporter's metadata.
   * Uses job name and instance (target_host) to uniquely identify the exporter's alerts.
   */
  const generateExporterMatchers = (exporter: Exporter): SilenceMatcher[] => {
    const matchers: SilenceMatcher[] = [];

    // Add job matcher - identifies the Prometheus job
    if (exporter.prometheus_job) {
      matchers.push({
        name: 'job',
        value: exporter.prometheus_job,
        isRegex: false,
      });
    }

    // Add instance matcher - identifies the specific target
    // The instance label in Prometheus is typically host:port format
    if (exporter.target_host) {
      matchers.push({
        name: 'instance',
        value: exporter.target_host,
        isRegex: false,
      });
    }

    return matchers;
  };

  const handleCreate = async () => {
    setError('');

    // Calculate final duration in minutes
    let durationMinutes = selectedDuration;
    if (selectedDuration === -1) {
      const hours = parseInt(customHours, 10) || 0;
      const minutes = parseInt(customMinutes, 10) || 0;
      durationMinutes = hours * 60 + minutes;
    }

    if (durationMinutes <= 0) {
      setError('Duration must be greater than zero');
      return;
    }

    // Warn for very long durations (> 7 days) but don't block
    const MAX_WARN_MINUTES = 7 * 24 * 60; // 7 days
    if (durationMinutes > MAX_WARN_MINUTES && !longDurationConfirmed) {
      const days = Math.floor(durationMinutes / (24 * 60));
      setError(`Warning: This silence will last ${days} days. Click "Create Silence" again to confirm.`);
      setLongDurationConfirmed(true);
      return;
    }

    const request: CreateSilenceRequest = {
      durationMinutes,
      comment,
    };

    if (silenceType === 'exporter' && selectedExporter) {
      // Generate matchers from exporter's prometheus_job and target_host
      // Backend requires matchers, not exporterId
      const matchers = generateExporterMatchers(selectedExporter);
      if (matchers.length === 0) {
        setError('Cannot create silence: exporter has no job name or target host configured');
        return;
      }
      request.matchers = matchers;
    } else if (silenceType === 'labels' && labelMatchers.trim()) {
      // Parse label matchers into structured format
      // Format: key="value", key2="value2"
      const parts = labelMatchers.split(',').map((m) => m.trim()).filter(Boolean);
      const matchers: SilenceMatcher[] = [];
      const invalidParts: string[] = [];

      for (const part of parts) {
        const match = part.match(/^(\w+)="([^"]*)"$/);
        if (match) {
          matchers.push({ name: match[1], value: match[2], isRegex: false });
        } else {
          invalidParts.push(part);
        }
      }

      if (invalidParts.length > 0) {
        setError(`Invalid matcher syntax: ${invalidParts.join(', ')}. Use format: name="value"`);
        return;
      }

      if (matchers.length === 0) {
        setError('No valid matchers found. Use format: name="value"');
        return;
      }

      request.matchers = matchers;
    }

    try {
      await onSubmit(request);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create silence');
    }
  };

  // Validation
  const isValid =
    comment.trim().length > 0 &&
    ((silenceType === 'exporter' && selectedExporter !== null) ||
      (silenceType === 'labels' && labelMatchers.trim().length > 0)) &&
    (selectedDuration > 0 ||
      (selectedDuration === -1 &&
        (parseInt(customHours, 10) > 0 || parseInt(customMinutes, 10) > 0)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: CARD_BORDER_RADIUS },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SilenceIcon color="primary" />
        Create Silence
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* What to silence */}
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            What to silence:
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <RadioGroup
              value={silenceType}
              onChange={(e) => setSilenceType(e.target.value as 'exporter' | 'labels')}
            >
              {/* Specific Exporter Option */}
              <FormControlLabel
                value="exporter"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Specific exporter
                    </Typography>
                  </Box>
                }
              />

              {/* Exporter Autocomplete */}
              {silenceType === 'exporter' && (
                <Box sx={{ ml: 4, mb: 2 }}>
                  <Autocomplete
                    options={exporters}
                    getOptionLabel={(option) => {
                      const displayName = option.display_name || option.name;
                      return option.target_host
                        ? `${displayName} — ${option.target_host}`
                        : displayName;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => {
                      const displayName = option.display_name || option.name;
                      const subtitle = [option.prometheus_job, option.target_host].filter(Boolean).join(' · ');
                      return (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2">{displayName}</Typography>
                            {subtitle && (
                              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                {subtitle}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search exporters..." />
                    )}
                    value={selectedExporter}
                    onChange={(_, newValue) => setSelectedExporter(newValue)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
                    }}
                  />
                </Box>
              )}

              {/* All alerts matching labels */}
              <FormControlLabel
                value="labels"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      All alerts matching labels
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Advanced - for silencing by severity, job, etc.)
                    </Typography>
                  </Box>
                }
              />

              {/* Label Matchers Input */}
              {silenceType === 'labels' && (
                <Box sx={{ ml: 4, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder='severity="info" or job="node_exporter"'
                    value={labelMatchers}
                    onChange={(e) => setLabelMatchers(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Use Prometheus label format: name="value"
                  </Typography>
                </Box>
              )}
            </RadioGroup>
          </FormControl>

          {/* Duration */}
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Duration:
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: showCustomFields ? 2 : 3 }}>
            {durationPresets.map((preset) => (
              <Button
                key={preset.value}
                variant={selectedDuration === preset.value ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleDurationChange(preset.value)}
                sx={{
                  textTransform: 'none',
                  borderRadius: CARD_BORDER_RADIUS,
                  minWidth: 80,
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          {/* Custom Duration Fields */}
          <Collapse in={showCustomFields}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                size="small"
                label="Hours"
                type="number"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                inputProps={{ min: 0, max: 168 }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
                }}
              />
              <TextField
                size="small"
                label="Minutes"
                type="number"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
                }}
              />
            </Box>
          </Collapse>

          {/* Comment */}
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Comment (required):
          </Typography>

          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="e.g., Scheduled maintenance window"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
            }}
          />

          {error && (
            <Alert severity={longDurationConfirmed ? 'warning' : 'error'} sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SilenceIcon />}
          sx={{ textTransform: 'none' }}
        >
          {loading ? 'Creating...' : 'Create Silence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateSilenceDialog;
